import styled, { keyframes } from 'styled-components'
import { Container, ContainerContent } from 'components/Containers/Containers'
import { AlarmBanner } from 'pages/Operations/Safety/Safety.styled'
import { tokens } from 'styling/tokens'
import { Head1, OtherText } from 'styling/Typography'
import { TankMetricLabel } from './Tanks/FuelTanks.styled'
import { DualTrack, LevelBarRow, LevelText, LevelTrack, TrackColumn, TrackLegend } from './Tanks/TankLevelBar.styled'

export const FuelTableContainer = styled.div`
  width: 100%;
  height: auto;
  padding: 1rem;
  ${(props) => props.theme.container};
  table {
    margin-top: 1rem;
  }
`

export const FuelPageTitle = styled(Head1)`
  font-size: 1.5rem;
  line-height: 1.3;
  color: ${tokens.text.primary};
`

export const FuelPageSubtitle = styled(OtherText)`
  margin: 0.25rem 0 1rem;
  color: ${tokens.text.muted};
`

export const HeaderLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  flex: none;
  padding: 0;
  border: none;
  background: transparent;
  font-size: ${tokens.font.label};
  font-weight: 500;
  color: ${tokens.accent.onDark};
  cursor: pointer;
  transition: color ${tokens.motion.duration} ${tokens.motion.ease};

  &:hover {
    color: ${tokens.accent.base};
  }
`

export const FuelAlarmBanner = styled(AlarmBanner)`
  margin: 0 0 1rem;
`

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

export const WidgetLoader = styled.div`
  width: 2rem;
  height: 2rem;
  border: 3px solid ${tokens.accent.soft};
  border-top-color: ${tokens.accent.base};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`

export const FuelKpiStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
`

export const FuelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 1rem;
  align-items: stretch;
`

export const FuelWidget = styled(Container)<{ $span: number }>`
  grid-column: span ${(props) => props.$span};
  display: flex;
  flex-direction: column;
  min-width: 0;

  @media (max-width: 1240px) {
    grid-column: span ${(props) => (props.$span >= 8 ? 12 : 6)};
  }

  @media (max-width: 860px) {
    grid-column: span 12;
  }
`

export const WidgetBody = styled(ContainerContent)`
  flex: 1;
  min-height: 0;
  max-height: 19rem;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: ${tokens.border.strong} transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${tokens.border.strong};
    border-radius: 999px;
  }
`

export const TankRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.375rem 0;
  border-bottom: 1px solid ${tokens.border.hairline};

  &:last-child {
    border-bottom: none;
  }
`

export const TankBarScale = styled.div`
  flex: none;

  ${TankMetricLabel} {
    display: none;
  }
  ${LevelText} {
    display: none;
  }
  ${TrackLegend} {
    display: none;
  }
  ${TrackColumn} {
    gap: 0;
  }
  ${LevelBarRow} {
    align-items: center;
    gap: 0;
  }
  ${LevelTrack} {
    width: 1rem;
    height: 2.25rem;
  }
  ${DualTrack} {
    width: calc(1rem + 2px);
    height: 2.25rem;
  }
`

export const TankRowText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`

export const TankRowName = styled.span`
  font-size: ${tokens.font.body};
  color: ${tokens.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const TankRowMeta = styled.span`
  font-size: ${tokens.font.micro};
  color: ${tokens.text.muted};
  font-variant-numeric: tabular-nums;
`

export const TankRowPct = styled.span`
  flex: none;
  width: 2.5rem;
  text-align: right;
  font-size: ${tokens.font.body};
  font-weight: 600;
  color: ${tokens.text.primary};
  font-variant-numeric: tabular-nums;
`

export const TankRowCover = styled.span<{ $color: string }>`
  flex: none;
  width: 4.5rem;
  text-align: right;
  font-size: ${tokens.font.label};
  color: ${(props) => props.$color};
  font-variant-numeric: tabular-nums;
`

export const ChartTickRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding-left: 2.5rem;
`

export const ChartTick = styled.span`
  font-size: ${tokens.font.micro};
  color: ${tokens.text.secondary};
  white-space: nowrap;
`

export const ChartNote = styled.div`
  margin-top: 0.5rem;
`

export const MachineRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.375rem 0;
  border-bottom: 1px solid ${tokens.border.hairline};
  cursor: pointer;
  transition: background ${tokens.motion.duration} ${tokens.motion.ease};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${tokens.surface.inset};
  }
`

export const MachineRowText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`

export const MachineRowName = styled.span`
  font-size: ${tokens.font.body};
  font-weight: 600;
  color: ${tokens.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const MachineRowMeta = styled.span`
  font-size: ${tokens.font.micro};
  color: ${tokens.text.muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const MachineRowRate = styled.span<{ $color: string }>`
  flex: none;
  font-size: ${tokens.font.body};
  font-weight: 600;
  color: ${(props) => props.$color};
  font-variant-numeric: tabular-nums;
`

export const DispenseLogFootnote = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-top: 1rem;
  border-top: 1px solid ${tokens.border.hairline};
`

export const BurnRateStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1rem;
`
